import { useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  TextField,
  Chip,
  FormLabel,
  Skeleton,
  Box,
  Tooltip,
  Typography,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import MaskAndValid from "utils/maskAndValid";
import ListboxComponent from "./list-box-virtualized";

const normalizeCnpjForSearch = (str) => (str || "").replace(/[./-]/g, "");

function createFilterOptionsWithCnpjNormalization(getLabel) {
  const defaultFilter = createFilterOptions();
  return (options, state) => {
    const inputValue = (state?.inputValue || "").trim();
    const defaultFiltered = defaultFilter(options, state);
    const normalizedInput = normalizeCnpjForSearch(inputValue);
    if (normalizedInput.length < 2) return defaultFiltered;
    const defaultSet = new Set(defaultFiltered);
    const extra = options.filter((option) => {
      if (defaultSet.has(option)) return false;
      const label = getLabel(option);
      const parts = `${label}`.split(/\s*\|\s*/);
      const rightPart = parts.length >= 2 ? parts[parts.length - 1] : "";
      const normalizedRight = normalizeCnpjForSearch(rightPart);
      return normalizedRight.includes(normalizedInput);
    });
    return extra.length ? [...defaultFiltered, ...extra] : defaultFiltered;
  };
}

export default function StoreField({
  value,
  storeList,
  disabled,
  onChange,
  loading,
  optionsSearchLoading = false,
  required,
  allStore = true,
  onSearchInputChange,
}) {
  const [selectedOptions, setSelectedOptions] = useState(value || []);
  const [controlledSearchInput, setControlledSearchInput] = useState("");
  const searchCallbackRef = useRef(onSearchInputChange);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    searchCallbackRef.current = onSearchInputChange;
  }, [onSearchInputChange]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setSelectedOptions(value || []);
  }, [value]);

  const storeNewList = (storeList || [])
    .filter((store) => store?.Id !== 0)
    .map((store) => ({
      Id: store?.Id,
      Nome: `${store?.Nome} | ${MaskAndValid.maskCNPJ(store?.CNPJ)}`,
      CNPJ: store?.CNPJ,
      Apelido:
        store?.Apelido !== " " || store?.Apelido?.length >= 2
          ? `${store?.Apelido} | ${MaskAndValid.maskCNPJ(store?.CNPJ)}`
          : `${store?.Nome} | ${MaskAndValid.maskCNPJ(store?.CNPJ)}`,
    }));

  if (storeNewList.length !== 1 && storeNewList.length !== 0 && allStore) {
    storeNewList.unshift({
      Id: "TODAS",
      Nome: "Todas",
      Apelido: "Todas",
    });
  }

  const handleChange = (event, newValue) => {
    let finalValue = newValue || [];

    if (newValue && newValue.some((option) => option.Id === "TODAS")) {
      finalValue = newValue.filter((option) => option.Id === "TODAS");
    }

    setSelectedOptions(finalValue);
    if (onChange) {
      onChange(finalValue);
    }
  };

  const removeOption = (optionId) => {
    const newOptions = selectedOptions.filter(
      (option) => option.Id !== optionId,
    );
    setSelectedOptions(newOptions);
    if (onChange) {
      onChange(newOptions);
    }
  };

  const isOptionEqualToValue = (option, value) => {
    return option?.Id === value?.Id;
  };

  const getOptionLabel = (option) => {
    if (typeof option === "string") return option;
    return option?.Nome || "";
  };

  const filterOptions = onSearchInputChange
    ? (options) => options
    : createFilterOptionsWithCnpjNormalization(getOptionLabel);

  const handleAutocompleteInputChange = (event, inputValue, reason) => {
    if (!onSearchInputChange) return;

    if (reason === "clear" || reason === "reset") {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
      return;
    }

    if (reason !== "input") return;

    setControlledSearchInput(inputValue ?? "");

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      const trimmed = (inputValue || "").trim();
      searchCallbackRef.current?.(trimmed.length >= 1 ? trimmed : "");
    }, 400);
  };

  const preserveInputWhileLoading = Boolean(onSearchInputChange);
  const listLoading =
    preserveInputWhileLoading && Boolean(loading || optionsSearchLoading);

  const getOptionApelido = (option) => {
    if (typeof option === "string") return option;
    return option?.Apelido || "";
  };

  return (
    <Box>
      <FormLabel
        sx={{
          color: themeColorsDesingSystem.text.primary,
          fontSize: ".875rem",
          fontWeight: "600",
          lineHeight: 1.5,
          mb: 0,
          fontFamily: "Open Sans,sans-serif",
        }}
      >
        Lojas
        {required ? " *" : ""}
      </FormLabel>
      {loading && !preserveInputWhileLoading ? (
        <Skeleton height="2.3em" sx={{ transform: "none" }} />
      ) : (
        <>
          <Autocomplete
            multiple
            disableCloseOnSelect={Boolean(onSearchInputChange)}
            options={storeNewList}
            value={selectedOptions}
            onChange={handleChange}
            disabled={disabled}
            loading={listLoading}
            {...(onSearchInputChange
              ? {
                  inputValue: controlledSearchInput,
                }
              : {})}
            onInputChange={
              onSearchInputChange ? handleAutocompleteInputChange : undefined
            }
            filterOptions={filterOptions}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            ListboxComponent={ListboxComponent}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: themeColorsDesingSystem.primary.main,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: themeColorsDesingSystem.stone.light,
                      cursor: "default",
                    },
                  },
                  "& .MuiInputBase-input": {
                    fontFamily: "Open Sans,sans-serif",
                    color: themeColorsDesingSystem.text.primary,
                    fontSize: ".875rem",
                    padding: "0 4px 0 5px!important",
                  },
                  "& .MuiIconButton-root": {
                    padding: 0,
                  },
                  "& .MuiAutocomplete-root": {
                    marginBottom: "0px",
                  },
                }}
              />
            )}
            renderTags={() => null}
            sx={{ mb: 1 }}
            renderOption={(props, option) => (
              <li {...props}>
                <Tooltip
                  arrow
                  title={getOptionApelido(option)}
                  leaveTouchDelay={100}
                  disableInteractive
                >
                  <Typography
                    noWrap
                    sx={{
                      fontFamily: "Open Sans,sans-serif",
                      color: themeColorsDesingSystem.text.primary,
                      fontSize: ".875rem",
                    }}
                  >
                    {getOptionLabel(option)}
                  </Typography>
                </Tooltip>
              </li>
            )}
          />

          {selectedOptions.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                overflowX: "auto",
                gap: 0.5,
                pb: 0.5,
                mb: 1,
                "&::-webkit-scrollbar": {
                  height: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#e0e0e0",
                  borderRadius: "4px",
                },
              }}
            >
              {selectedOptions.map((option) => (
                <Chip
                  key={option.Id}
                  label={option.Nome}
                  onDelete={() => removeOption(option.Id)}
                  sx={{
                    backgroundColor: "#f5f5f5",
                    color: themeColorsDesingSystem.text.primary,
                    "& .MuiChip-deleteIcon": {
                      color: "#666",
                    },
                    mr: 0.5,
                  }}
                />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
